/**
 * Action Properties - Shared Constants
 * Centralized constants for roles, icons, and variants used across all actions
 * Eliminates duplication and provides a single source of truth
 */

import { HUB_ROLE } from "@/lib/auth/roles";

/**
 * Role constants used in action visibility/access control.
 *
 * Derived from `HUB_ROLE` rather than re-typed: this was a third hand-written
 * copy of the role vocabulary, and it had drifted — it carried a fifth entry,
 * `USER: "wtc_user"`, for a role that does not exist. Hub has exactly four
 * (see `docs/engineering-rules/USER_ROLES_AND_ACCESS.md`), the string appeared
 * nowhere else in the repo, and an `allowedRoles` naming it would have matched
 * nobody while reading like a deliberate grant.
 */
export const ACTION_ROLES = {
  ADMIN: HUB_ROLE.ADMIN,
  EMPLOYEE: HUB_ROLE.EMPLOYEE,
  /** Hub CRM role — same product access as employees for row/detail actions */
  CRM: HUB_ROLE.CRM,
  CLIENT_USER: HUB_ROLE.CLIENT,
} as const;

/**
 * Icon constants used in action buttons
 * Maps semantic names to icon identifiers
 */
export const ACTION_ICONS = {
  // CRUD operations
  EDIT: "edit",
  DELETE: "trash",
  DUPLICATE: "copy",
  COPY_LINK: "link",
  VIEW: "eye",

  // Communication
  MAIL: "mail",
  PHONE: "phone",
  ACTIVITY: "activity",
  TICKET: "ticket",
  CLOCK: "clock",
  CALENDAR: "calendar",
  MESSAGE: "message-square",

  // Status/State
  CHEVRON_DOWN: "chevron-down",
  CHECK_CIRCLE: "check-circle",
  X_CIRCLE: "x-circle",

  // Organization
  BUILDING: "building",
  USERS: "users",
  USER: "user",
  SHIELD: "shield",

  // Actions
  PLUS: "plus",
  SEARCH: "search",
  DOWNLOAD: "download",
  GIT_MERGE: "git-merge",
  REFRESH: "refresh-cw",

  // Integration
  FILE_TEXT: "file-text",
  SPARKLES: "sparkles",
  MERIDIAN: "meridian",
} as const;

/**
 * Button variant constants
 * Defines visual styling for action buttons
 */
export const ACTION_VARIANTS = {
  DEFAULT: "default",
  OUTLINE: "outline",
  DESTRUCTIVE: "destructive",
  SUCCESS: "success",
  SECONDARY: "secondary",
  GHOST: "ghost",
} as const;

/**
 * Common role combinations used across entities
 */
export const ROLE_COMBINATIONS = {
  ADMIN_ONLY: [ACTION_ROLES.ADMIN] as const,
  /** Employees, CRM users, and admins (excludes client_user) */
  EMPLOYEE_AND_ADMIN: [ACTION_ROLES.EMPLOYEE, ACTION_ROLES.ADMIN, ACTION_ROLES.CRM] as const,
  ALL_AUTHENTICATED: [
    ACTION_ROLES.CLIENT_USER,
    ACTION_ROLES.EMPLOYEE,
    ACTION_ROLES.ADMIN,
    ACTION_ROLES.CRM,
  ] as const,
} as const;

